import React, { useState, useMemo, useCallback } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { base44 } from '@/api/base44Client';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AlertTriangle, Search, Package, Tag, Calendar, Filter, BarChart3,
  TrendingDown, User, X, FileText, Edit2, Trash2, Download, CheckCircle2, Clock3, ChevronUp
} from 'lucide-react';
import { useBatchSelection } from '@/hooks/useBatchSelection';
import BatchActionBar from '@/components/ui/BatchActionBar';
import { format } from 'date-fns';
import { id as idLocale } from 'date-fns/locale';
import { toast } from 'react-hot-toast';
import CustomDatePicker from '@/components/shared/CustomDatePicker';

const ACTIVITY_LABELS = {
  filling: 'Filling', packing: 'Pengemasan', packaging: 'Pengemasan',
  racik: 'Racik / Mixing', labeling: 'Pengemasan', qc: 'QC', other: 'Lainnya',
};
const ACTIVITY_COLORS = {
  filling: 'bg-cyan-100 text-cyan-700 border-cyan-200',
  packing: 'bg-orange-100 text-orange-700 border-orange-200',
  packaging: 'bg-orange-100 text-orange-700 border-orange-200',
  racik: 'bg-purple-100 text-purple-700 border-purple-200',
  labeling: 'bg-orange-100 text-orange-700 border-orange-200',
  qc: 'bg-green-100 text-green-700 border-green-200',
  other: 'bg-slate-100 text-slate-700 border-slate-200',
};
const fmt = (n) => n != null ? new Intl.NumberFormat('id-ID', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n) : '—';

export default function RejectTracking() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [activityFilter, setActivityFilter] = useState('semua');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  // Edit modal
  const [editModal, setEditModal] = useState(null); // { ...rejectItem }
  const [editQty, setEditQty] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editItemName, setEditItemName] = useState('');
  const [saving, setSaving] = useState(false);

  const { data: allTasks = [], isLoading } = useQuery({
    queryKey: ['production-tasks-all'],
    queryFn: () => base44.entities.ProductionTask.list('-created_date'),
  });
  const { data: allOrders = [] } = useQuery({
    queryKey: ['production-orders-reject'],
    queryFn: () => base44.entities.ProductionOrder.list('-created_date'),
  });

  // Extract reject items with stable unique keys
  const allRejects = useMemo(() => {
    const rejects = [];
    allTasks.forEach(task => {
      if (!task.member_progress) return;
      Object.entries(task.member_progress).forEach(([memberId, prog]) => {
        if (prog.status !== 'completed' || !prog.rejected_items?.length) return;
        prog.rejected_items.forEach((rj, rjIdx) => {
          rj.items.forEach((item, itemIdx) => {
            if (!item.name || !item.qty) return;
            const order = allOrders.find(o => o.id === task.po_id);
            rejects.push({
              id: `${task.id}__${memberId}__${rjIdx}__${itemIdx}`,
              taskId: task.id,
              memberId,
              rjIdx,
              itemIdx,
              taskName: task.task_name,
              poId: task.po_id,
              poNumber: task.po_number || order?.order_number || '—',
              poBrand: task.po_brand || order?.brand_name || '—',
              activity: task.activity_type,
              picName: task.pic_name || 'Operator',
              variantName: rj.variantName || 'Produk Utama',
              itemName: item.name,
              qty: Number(item.qty) || 0,
              notes: item.notes || '',
              date: prog.end_time || task.end_time || task.created_date,
              isConfirmed: !!item.is_confirmed_by_finance,
            });
          });
        });
      });
    });
    rejects.sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));
    return rejects;
  }, [allTasks, allOrders]);

  // Filters
  const filteredRejects = useMemo(() => {
    const q = search.toLowerCase();
    return allRejects.filter(rj => {
      if (activityFilter !== 'semua') {
        if (activityFilter === 'packing' && rj.activity !== 'packing' && rj.activity !== 'packaging' && rj.activity !== 'labeling') return false;
        if (activityFilter !== 'packing' && rj.activity !== activityFilter) return false;
      }
      if (dateFrom && rj.date) { if (new Date(rj.date).toISOString().split('T')[0] < dateFrom) return false; }
      if (dateTo && rj.date) { if (new Date(rj.date).toISOString().split('T')[0] > dateTo) return false; }
      if (q) {
        return rj.itemName.toLowerCase().includes(q) || rj.poBrand.toLowerCase().includes(q) ||
          rj.poNumber.toString().includes(q) || rj.variantName.toLowerCase().includes(q) ||
          rj.picName.toLowerCase().includes(q) || rj.notes.toLowerCase().includes(q);
      }
      return true;
    });
  }, [allRejects, activityFilter, dateFrom, dateTo, search]);

  const { selectedIds, isSelectionMode, toggleSelection, selectAll, clearSelection, startSelection } = useBatchSelection(filteredRejects);

  // Stats
  const stats = useMemo(() => {
    const totalQty = filteredRejects.reduce((sum, rj) => sum + rj.qty, 0);
    const uniquePOs = new Set(filteredRejects.map(rj => rj.poId)).size;
    const uniqueItems = new Set(filteredRejects.map(rj => rj.itemName)).size;
    const itemMap = {};
    filteredRejects.forEach(rj => { if (!itemMap[rj.itemName]) itemMap[rj.itemName] = 0; itemMap[rj.itemName] += rj.qty; });
    const topItems = Object.entries(itemMap).sort((a, b) => b[1] - a[1]).slice(0, 5);
    return { totalQty, uniquePOs, uniqueItems, topItems };
  }, [filteredRejects]);

  // ===== HELPERS: mutate member_progress =====
  const getTaskAndPath = useCallback((rejectItem) => {
    const task = allTasks.find(t => t.id === rejectItem.taskId);
    if (!task) return null;
    return task;
  }, [allTasks]);

  // ===== EDIT =====
  const handleOpenEdit = (rj) => {
    setEditModal(rj);
    setEditItemName(rj.itemName);
    setEditQty(String(rj.qty));
    setEditNotes(rj.notes);
  };

  const handleSaveEdit = async () => {
    if (!editModal) return;
    setSaving(true);
    try {
      const task = getTaskAndPath(editModal);
      if (!task) throw new Error('Task not found');
      const mp = JSON.parse(JSON.stringify(task.member_progress || {}));
      const prog = mp[editModal.memberId];
      if (!prog?.rejected_items?.[editModal.rjIdx]?.items?.[editModal.itemIdx]) throw new Error('Reject item not found');
      prog.rejected_items[editModal.rjIdx].items[editModal.itemIdx] = {
        ...prog.rejected_items[editModal.rjIdx].items[editModal.itemIdx],
        name: editItemName,
        qty: editQty,
        notes: editNotes,
      };
      await base44.entities.ProductionTask.update(task.id, { member_progress: mp });
      queryClient.invalidateQueries({ queryKey: ['production-tasks-all'] });
      toast.success('Catatan reject berhasil diperbarui!');
      setEditModal(null);
    } catch (e) {
      console.error(e);
      toast.error('Gagal menyimpan perubahan');
    }
    setSaving(false);
  };

  // ===== DELETE (single or batch) =====
  const deleteRejectItems = async (rejectIds) => {
    // Group by taskId+memberId for efficient updates
    const grouped = {};
    rejectIds.forEach(id => {
      const rj = allRejects.find(r => r.id === id);
      if (!rj) return;
      const key = `${rj.taskId}__${rj.memberId}`;
      if (!grouped[key]) grouped[key] = { taskId: rj.taskId, memberId: rj.memberId, toRemove: [] };
      grouped[key].toRemove.push({ rjIdx: rj.rjIdx, itemIdx: rj.itemIdx });
    });

    for (const g of Object.values(grouped)) {
      const task = allTasks.find(t => t.id === g.taskId);
      if (!task) continue;
      const mp = JSON.parse(JSON.stringify(task.member_progress || {}));
      const prog = mp[g.memberId];
      if (!prog?.rejected_items) continue;

      // Sort removals in reverse order so indices don't shift
      const sorted = g.toRemove.sort((a, b) => {
        if (a.rjIdx !== b.rjIdx) return b.rjIdx - a.rjIdx;
        return b.itemIdx - a.itemIdx;
      });
      for (const rm of sorted) {
        if (prog.rejected_items[rm.rjIdx]?.items) {
          prog.rejected_items[rm.rjIdx].items.splice(rm.itemIdx, 1);
        }
      }
      // Clean up empty variant groups
      prog.rejected_items = prog.rejected_items.filter(rj => rj.items && rj.items.length > 0);
      if (prog.rejected_items.length === 0) delete prog.rejected_items;

      await base44.entities.ProductionTask.update(task.id, { member_progress: mp });
    }
    queryClient.invalidateQueries({ queryKey: ['production-tasks-all'] });
  };

  const handleBatchDelete = async () => {
    if (!confirm(`Hapus ${selectedIds.size} catatan reject? Data ini akan dihapus permanen dari tugas produksi.`)) return;
    try {
      await deleteRejectItems(Array.from(selectedIds));
      clearSelection();
      toast.success(`${selectedIds.size} catatan reject berhasil dihapus`);
    } catch (e) {
      console.error(e);
      toast.error('Gagal menghapus beberapa catatan');
    }
  };

  const handleSingleDelete = async (rj) => {
    if (!confirm(`Hapus catatan reject "${rj.itemName}" (${rj.qty} pcs)?`)) return;
    try {
      await deleteRejectItems([rj.id]);
      toast.success('Catatan reject dihapus');
    } catch (e) {
      toast.error('Gagal menghapus');
    }
  };

  // ===== EXPORT PDF =====
  const handleExportPDF = () => {
    const dataToExport = filteredRejects;
    if (dataToExport.length === 0) { toast.error('Tidak ada data untuk diexport'); return; }

    const now = new Date();
    const dateStr = format(now, 'dd MMMM yyyy, HH:mm', { locale: idLocale });
    const filterInfo = [];
    if (activityFilter !== 'semua') filterInfo.push(`Proses: ${ACTIVITY_LABELS[activityFilter] || activityFilter}`);
    if (dateFrom) filterInfo.push(`Dari: ${dateFrom}`);
    if (dateTo) filterInfo.push(`Sampai: ${dateTo}`);
    if (search) filterInfo.push(`Pencarian: "${search}"`);

    const totalQty = dataToExport.reduce((s, r) => s + r.qty, 0);

    const html = `
<!DOCTYPE html>
<html><head>
<meta charset="UTF-8">
<title>Laporan Reject Produksi — Satala Dermatech Essential</title>
<style>
  * { margin: 0; padding: 0; box-sizing: border-box; }
  body { font-family: 'Segoe UI', system-ui, sans-serif; color: #1e293b; font-size: 11px; padding: 24px; }
  .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 20px; border-bottom: 3px solid #302621; padding-bottom: 16px; }
  .header h1 { font-size: 20px; font-weight: 900; color: #302621; }
  .header .sub { font-size: 10px; color: #8C6208; font-weight: 700; text-transform: uppercase; letter-spacing: 1px; }
  .header .date { font-size: 10px; color: #64748b; text-align: right; }
  .filter-info { background: #f8fafc; border: 1px solid #e2e8f0; border-radius: 6px; padding: 8px 12px; margin-bottom: 16px; font-size: 10px; color: #475569; }
  .summary { display: flex; gap: 16px; margin-bottom: 20px; }
  .summary-card { flex: 1; background: #fef2f2; border: 1px solid #fecaca; border-radius: 8px; padding: 10px 14px; text-align: center; }
  .summary-card .value { font-size: 22px; font-weight: 900; color: #dc2626; }
  .summary-card .label { font-size: 9px; color: #ef4444; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; margin-top: 2px; }
  .summary-card.blue { background: #eff6ff; border-color: #bfdbfe; }
  .summary-card.blue .value { color: #2563eb; }
  .summary-card.blue .label { color: #3b82f6; }
  table { width: 100%; border-collapse: collapse; margin-top: 8px; }
  th { background: #302621; color: white; padding: 8px 10px; text-align: left; font-size: 9px; font-weight: 800; text-transform: uppercase; letter-spacing: 0.5px; }
  td { padding: 7px 10px; border-bottom: 1px solid #e2e8f0; font-size: 10px; vertical-align: top; }
  tr:nth-child(even) { background: #f8fafc; }
  tr:hover { background: #fef2f2; }
  .badge { display: inline-block; padding: 2px 6px; border-radius: 4px; font-size: 9px; font-weight: 700; }
  .badge-red { background: #fee2e2; color: #dc2626; }
  .badge-blue { background: #dbeafe; color: #2563eb; }
  .badge-amber { background: #fef3c7; color: #d97706; }
  .footer { margin-top: 24px; padding-top: 12px; border-top: 1px solid #e2e8f0; font-size: 9px; color: #94a3b8; display: flex; justify-content: space-between; }
  @media print { body { padding: 12px; } }
</style>
</head><body>
<div class="header">
  <div>
    <h1>📋 Laporan Reject Produksi</h1>
    <div class="sub">Satala Dermatech Essential</div>
  </div>
  <div class="date">Dicetak: ${dateStr}<br/>Total: ${dataToExport.length} catatan</div>
</div>
${filterInfo.length > 0 ? `<div class="filter-info">Filter aktif: ${filterInfo.join(' · ')}</div>` : ''}
<div class="summary">
  <div class="summary-card"><div class="value">${fmt(totalQty)}</div><div class="label">Total Pcs Reject</div></div>
  <div class="summary-card blue"><div class="value">${dataToExport.length}</div><div class="label">Catatan Reject</div></div>
  <div class="summary-card blue"><div class="value">${new Set(dataToExport.map(r => r.poId)).size}</div><div class="label">PO Terdampak</div></div>
</div>
<table>
<thead><tr><th>No</th><th>Tanggal</th><th>Brand</th><th>Varian</th><th>Item Reject</th><th>Qty</th><th>Proses</th><th>PIC / Operator</th><th>Catatan</th></tr></thead>
<tbody>
${dataToExport.map((rj, i) => `<tr>
  <td>${i + 1}</td>
  <td>${rj.date ? format(new Date(rj.date), 'dd/MM/yyyy HH:mm') : '—'}</td>
  <td><strong>${rj.poBrand}</strong></td>
  <td>${rj.variantName}</td>
  <td><strong>${rj.itemName}</strong></td>
  <td><span class="badge badge-red">${rj.qty} pcs</span></td>
  <td><span class="badge badge-blue">${ACTIVITY_LABELS[rj.activity] || rj.activity}</span></td>
  <td>${rj.picName}</td>
  <td style="max-width:160px;word-break:break-word;">${rj.notes || '—'}</td>
</tr>`).join('')}
</tbody>
</table>
<div class="footer">
  <span>Digenerate otomatis oleh sistem Satala Dermatech Essential</span>
  <span>Halaman 1</span>
</div>
</body></html>`;

    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const win = window.open(url, '_blank');
    if (win) {
      win.onload = () => { setTimeout(() => { win.print(); }, 500); };
    }
    toast.success('PDF siap dicetak!');
  };

  const activityFilterOptions = [
    { key: 'semua', label: 'Semua' },
    { key: 'filling', label: 'Filling' },
    { key: 'packing', label: 'Pengemasan' },
    { key: 'racik', label: 'Racik' },
    { key: 'qc', label: 'QC' },
  ];

  const clearFilters = () => { setSearch(''); setActivityFilter('semua'); setDateFrom(''); setDateTo(''); };
  const hasActiveFilters = search || activityFilter !== 'semua' || dateFrom || dateTo;

  return (
    <div className="space-y-6 text-[#302621]">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-red-500 to-rose-600 flex items-center justify-center shadow-lg shadow-red-200">
            <AlertTriangle className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-black text-slate-800 tracking-tight">Laporan Reject Produksi</h1>
            <p className="text-xs text-slate-500 mt-0.5">
              Rekap seluruh item reject/cacat dari proses produksi — {allRejects.length} total catatan.
            </p>
          </div>
        </div>
        <Button onClick={handleExportPDF} variant="outline" className="gap-2 text-xs font-bold h-9 border-[#E6D5B8] text-[#8C6208] hover:bg-[#F9F5EC]">
          <Download className="w-3.5 h-3.5" /> Export PDF
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: 'Total Reject', value: fmt(stats.totalQty), sub: 'pcs item rusak', icon: TrendingDown, gradient: 'from-red-50 to-rose-50', border: 'border-red-100', textCls: 'text-red-700', iconCls: 'text-red-500', subCls: 'text-red-400' },
          { label: 'Catatan', value: filteredRejects.length, sub: 'laporan tercatat', icon: FileText, gradient: 'from-amber-50 to-yellow-50', border: 'border-amber-100', textCls: 'text-amber-700', iconCls: 'text-amber-500', subCls: 'text-amber-400' },
          { label: 'PO Terdampak', value: stats.uniquePOs, sub: 'production order', icon: Package, gradient: 'from-blue-50 to-indigo-50', border: 'border-blue-100', textCls: 'text-blue-700', iconCls: 'text-blue-500', subCls: 'text-blue-400' },
          { label: 'Jenis Item', value: stats.uniqueItems, sub: 'tipe item berbeda', icon: Tag, gradient: 'from-purple-50 to-fuchsia-50', border: 'border-purple-100', textCls: 'text-purple-700', iconCls: 'text-purple-500', subCls: 'text-purple-400' },
        ].map(s => {
          const Icon = s.icon;
          return (
            <Card key={s.label} className={`${s.border} bg-gradient-to-br ${s.gradient}`}>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Icon className={`w-4 h-4 ${s.iconCls}`} />
                  <span className={`text-[10px] font-bold uppercase tracking-wider ${s.subCls}`}>{s.label}</span>
                </div>
                <p className={`text-2xl font-black ${s.textCls}`}>{s.value}</p>
                <p className={`text-[10px] mt-0.5 ${s.subCls}`}>{s.sub}</p>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Top Rejected */}
      {stats.topItems.length > 0 && (
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <h3 className="text-xs font-black text-slate-500 uppercase tracking-wider mb-3 flex items-center gap-2">
              <BarChart3 className="w-3.5 h-3.5" /> Item Paling Sering Reject
            </h3>
            <div className="flex flex-wrap gap-2">
              {stats.topItems.map(([name, qty], idx) => (
                <div key={name} className={`flex items-center gap-2 px-3 py-2 rounded-xl border ${idx === 0 ? 'bg-red-50 border-red-200 text-red-700' : idx === 1 ? 'bg-amber-50 border-amber-200 text-amber-700' : 'bg-slate-50 border-slate-200 text-slate-700'}`}>
                  <span className={`text-[10px] font-black rounded-full w-5 h-5 flex items-center justify-center ${idx === 0 ? 'bg-red-200 text-red-800' : idx === 1 ? 'bg-amber-200 text-amber-800' : 'bg-slate-200 text-slate-800'}`}>{idx + 1}</span>
                  <span className="text-xs font-bold">{name}</span>
                  <span className="text-[10px] font-black opacity-60">{qty} pcs</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Search & Filters */}
      <div className="space-y-3">
        <div className="flex items-center gap-3 flex-wrap">
          <div className="flex-1 min-w-[200px] relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari item, brand, PO, operator..." className="pl-9 h-9 text-sm" />
          </div>
          <Button variant="outline" size="sm" onClick={() => setShowFilters(v => !v)} className={`gap-1.5 text-xs h-9 ${showFilters ? 'bg-[#F9F5EC] text-[#8C6208] border-[#E6D5B8]' : ''}`}>
            <Filter className="w-3.5 h-3.5" /> Filter
            {hasActiveFilters && <span className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />}
          </Button>
          {hasActiveFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters} className="gap-1 text-xs h-9 text-slate-500"><X className="w-3 h-3" /> Reset</Button>
          )}
        </div>
        {showFilters && (
          <Card className="border-[#E6D5B8] bg-[#FFFDF9]">
            <CardContent className="p-4 space-y-3">
              <div>
                <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2 block">Proses Produksi</label>
                <div className="flex flex-wrap gap-1.5">
                  {activityFilterOptions.map(opt => (
                    <button key={opt.key} onClick={() => setActivityFilter(opt.key)}
                      className={`px-3 py-1.5 rounded-lg border text-[10px] font-bold uppercase tracking-wider transition-all ${activityFilter === opt.key ? 'bg-[#F9F5EC] text-[#8C6208] border-[#E6D5B8] shadow-sm' : 'bg-white text-slate-500 border-slate-200 hover:border-[#E6D5B8]'}`}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1 block">Dari Tanggal</label>
                  <CustomDatePicker value={dateFrom} onChange={setDateFrom} className="h-8 text-xs" />
                </div>
                <div>
                  <label className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-1 block">Sampai Tanggal</label>
                  <CustomDatePicker value={dateTo} onChange={setDateTo} className="h-8 text-xs" />
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Reject List */}
      {isLoading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-8 h-8 border-4 border-red-200 border-t-red-500 rounded-full animate-spin" />
        </div>
      ) : filteredRejects.length === 0 ? (
        <div className="text-center py-16">
          <div className="w-16 h-16 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-4">
            <Package className="w-8 h-8 text-emerald-300" />
          </div>
          <h3 className="text-sm font-bold text-slate-600">
            {allRejects.length === 0 ? 'Belum ada catatan reject!' : 'Tidak ada reject yang cocok dengan filter.'}
          </h3>
          <p className="text-xs text-slate-400 mt-1">
            {allRejects.length === 0
              ? 'Catatan reject akan muncul otomatis saat operator menyelesaikan tugas dan melaporkan item cacat.'
              : 'Coba ubah filter atau kata kunci pencarian.'}
          </p>
        </div>
      ) : (
        <div className="space-y-2">
          {filteredRejects.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage).map((rj, idx) => {
            const actCls = ACTIVITY_COLORS[rj.activity] || ACTIVITY_COLORS.other;
            const isSelected = selectedIds.has(rj.id);
            return (
              <Card key={rj.id}
                className={`border-slate-200 hover:border-red-200 hover:shadow-sm transition-all ${isSelectionMode && isSelected ? 'ring-2 ring-rose-500 !bg-rose-50/80' : ''} ${isSelectionMode ? 'cursor-pointer' : ''}`}
                onClick={() => { if (isSelectionMode) toggleSelection(rj.id); }}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    {/* Checkbox / Icon */}
                    {isSelectionMode ? (
                      <div className="pt-1" onClick={e => e.stopPropagation()}>
                        <Checkbox checked={isSelected} onCheckedChange={() => toggleSelection(rj.id)} />
                      </div>
                    ) : (
                      <div className="w-10 h-10 rounded-xl bg-red-50 border border-red-100 flex items-center justify-center shrink-0">
                        <AlertTriangle className="w-5 h-5 text-red-400" />
                      </div>
                    )}

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className="font-bold text-sm text-slate-800">{rj.itemName}</span>
                        <Badge className="text-[10px] bg-red-100 text-red-700 border-red-200 shadow-none font-black">{rj.qty} pcs</Badge>
                        <Badge className={`text-[10px] shadow-none font-bold ${actCls}`}>{ACTIVITY_LABELS[rj.activity] || rj.activity}</Badge>
                      </div>
                      <div className="flex items-center gap-3 text-[11px] text-slate-500 flex-wrap">
                        <span className="flex items-center gap-1 font-semibold text-[#8C6208] bg-[#F9F5EC] px-1.5 py-0.5 rounded">
                          <Package className="w-3 h-3" /> {rj.poBrand}
                        </span>
                        <span className="text-slate-400">PO #{rj.poNumber}</span>
                        <span className="text-slate-400">·</span>
                        <span className="italic text-slate-500">{rj.variantName}</span>
                      </div>
                      {rj.notes && (
                        <p className="text-xs text-slate-400 italic mt-1.5 bg-slate-50 px-2 py-1 rounded border border-slate-100">
                          💬 "{rj.notes}"
                        </p>
                      )}
                      <div className="flex items-center gap-4 mt-2 text-[10px] text-slate-400">
                        <span className="flex items-center gap-1"><User className="w-3 h-3" /> {rj.picName}</span>
                        {rj.date && (
                          <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {format(new Date(rj.date), 'dd MMM yyyy, HH:mm', { locale: idLocale })}</span>
                        )}
                        {rj.isConfirmed ? (
                          <span className="flex items-center gap-1 text-emerald-600 font-bold bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-100">
                            <CheckCircle2 className="w-3 h-3" /> Dikonfirmasi Finance
                          </span>
                        ) : (
                          <span className="flex items-center gap-1 text-amber-600 font-bold bg-amber-50 px-2 py-0.5 rounded-full border border-amber-100">
                            <Clock3 className="w-3 h-3" /> Menunggu Finance
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Actions */}
                    {!isSelectionMode && (
                      <div className="flex gap-1 shrink-0 items-center">
                        <Button variant="ghost" size="icon" className="w-8 h-8 hover:bg-slate-100" onClick={e => { e.stopPropagation(); handleOpenEdit(rj); }}>
                          <Edit2 className="w-3.5 h-3.5 text-slate-500" />
                        </Button>
                        <Button variant="ghost" size="icon" className="w-8 h-8 text-rose-500 hover:bg-rose-50" onClick={e => { e.stopPropagation(); startSelection(rj.id); }}>
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    )}

                    {/* Qty highlight desktop */}
                    <div className="text-right shrink-0 hidden sm:block">
                      <p className="text-2xl font-black text-red-600">{rj.qty}</p>
                      <p className="text-[10px] text-red-400 font-bold">pcs</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
          
          {/* ── Pagination Controls ── */}
          {filteredRejects.length > 0 && (
            <div className="px-6 py-4 border border-slate-100 rounded-2xl bg-white flex flex-wrap items-center justify-between gap-4 mt-6">
              <div className="flex items-center gap-3">
                <span className="text-[11px] font-bold text-slate-500">Row per Page</span>
                <div className="w-[80px]">
                  <Select value={String(itemsPerPage)} onValueChange={(val) => { setItemsPerPage(Number(val)); setCurrentPage(1); }}>
                    <SelectTrigger className="h-8 text-[11px] font-bold text-slate-700 bg-slate-50 border-slate-200/80 rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[5, 10, 25, 50, 100].map(n => (
                        <SelectItem key={n} value={String(n)} className="text-[11px] font-bold">{n}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="flex items-center gap-1.5">
                <button 
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-50 hover:text-[#8C6208] disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-400 transition-all font-bold text-sm"
                >
                  &lt;
                </button>
                
                {(() => {
                  const totalPages = Math.ceil(filteredRejects.length / itemsPerPage);
                  const pages = [];
                  for(let i=1; i<=totalPages; i++) {
                    if (i === 1 || i === totalPages || (i >= currentPage - 1 && i <= currentPage + 1)) {
                      pages.push(
                        <button
                          key={i}
                          onClick={() => setCurrentPage(i)}
                          className={`w-8 h-8 flex items-center justify-center rounded-lg text-[11px] font-black transition-all ${
                            currentPage === i 
                              ? 'bg-[#8C6208] text-white shadow-md shadow-[#8C6208]/30' 
                              : 'text-slate-500 hover:bg-[#F9F5EC] hover:text-[#8C6208]'
                          }`}
                        >
                          {i}
                        </button>
                      );
                    } else if (i === currentPage - 2 || i === currentPage + 2) {
                      pages.push(<span key={i} className="text-slate-400 text-[10px] px-1">...</span>);
                    }
                  }
                  return pages;
                })()}
                
                <button 
                  onClick={() => setCurrentPage(p => Math.min(Math.ceil(filteredRejects.length / itemsPerPage), p + 1))}
                  disabled={currentPage >= Math.ceil(filteredRejects.length / itemsPerPage)}
                  className="w-8 h-8 flex items-center justify-center rounded-lg text-slate-400 hover:bg-slate-50 hover:text-[#8C6208] disabled:opacity-30 disabled:hover:bg-transparent disabled:hover:text-slate-400 transition-all font-bold text-sm"
                >
                  &gt;
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Floating Back to Top Button */}
      <button 
        onClick={scrollToTop}
        className="fixed bottom-[80px] md:bottom-8 right-6 md:right-8 z-50 w-12 h-12 bg-[#8C6208] hover:bg-[#6A4A06] text-white rounded-full shadow-xl shadow-[#8C6208]/30 flex items-center justify-center transition-transform hover:-translate-y-1 print:hidden border border-[#D4AF37]/30"
        title="Kembali ke Atas"
      >
        <ChevronUp className="w-6 h-6" />
      </button>

      {/* Batch Action Bar */}
      <BatchActionBar
        isSelectionMode={isSelectionMode}
        selectedCount={selectedIds.size}
        totalCount={filteredRejects.length}
        onSelectAll={selectAll}
        onClearSelection={clearSelection}
        onBatchDelete={handleBatchDelete}
        isDeleting={false}
      />

      {/* Edit Modal */}
      {editModal && (
        <div className="fixed inset-0 bg-slate-900/60  z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl border border-slate-200 shadow-2xl max-w-md w-full overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100 flex justify-between items-center bg-gradient-to-r from-red-50 to-rose-50">
              <h3 className="font-black text-red-700 text-sm flex items-center gap-2">
                <Edit2 className="w-4 h-4" /> Edit Catatan Reject
              </h3>
              <button onClick={() => setEditModal(null)} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-5 space-y-4">
              {/* Context info */}
              <div className="bg-slate-50 rounded-xl p-3 space-y-1 border border-slate-100">
                <div className="flex items-center gap-2 text-xs">
                  <Badge className="text-[10px] bg-[#F9F5EC] text-[#8C6208] border-[#E6D5B8]">{editModal.poBrand}</Badge>
                  <span className="text-slate-400">·</span>
                  <span className="text-slate-500 italic">{editModal.variantName}</span>
                </div>
                <p className="text-[10px] text-slate-400">PO #{editModal.poNumber} · {ACTIVITY_LABELS[editModal.activity]} · PIC: {editModal.picName}</p>
              </div>

              <div>
                <label className="text-xs font-bold text-slate-600 block mb-1">Nama Item Reject *</label>
                <Input value={editItemName} onChange={(e) => setEditItemName(e.target.value)} placeholder="Contoh: Botol, Tutup, Spray..." className="text-xs" />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-600 block mb-1">Jumlah Reject (pcs) *</label>
                <Input type="number" value={editQty} onChange={(e) => setEditQty(e.target.value)} placeholder="0" className="text-xs" />
              </div>
              <div>
                <label className="text-xs font-bold text-slate-600 block mb-1">Catatan / Alasan</label>
                <Input value={editNotes} onChange={(e) => setEditNotes(e.target.value)} placeholder="Contoh: Pecah saat proses filling..." className="text-xs" />
              </div>
            </div>
            <div className="px-5 py-4 border-t border-slate-100 bg-slate-50 flex justify-end gap-2">
              <Button variant="ghost" size="sm" className="text-xs font-bold" onClick={() => setEditModal(null)}>Batal</Button>
              <Button size="sm" className="bg-red-600 hover:bg-red-700 text-white text-xs font-bold gap-1.5" onClick={handleSaveEdit} disabled={saving || !editItemName || !editQty}>
                {saving ? 'Menyimpan...' : 'Simpan Perubahan'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
